import { ApiError } from '@/api/client';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  compact?: boolean;
  className?: string;
}

/** Error + retry for every list; the request id is shown so a toast is never the only place it lives. */
export function ErrorState({ error, onRetry, title = "Couldn't load", compact, className }: ErrorStateProps) {
  const say = useWording();
  const apiError = error instanceof ApiError ? error : null;
  const message = say(apiError?.message ?? (error instanceof Error ? error.message : 'Unexpected error'));
  return (
    <div role="alert" className={cn('flex flex-col items-center gap-3 text-center', compact ? 'px-4 py-8' : 'px-6 py-12', className)}>
      <div className="flex size-12 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10 text-destructive">
        <Icon name="error" size={24} />
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        <p className="mx-auto mt-1 max-w-[48ch] text-[13px] text-muted-foreground">{message}</p>
        {apiError?.requestId ? <p className="mt-1 font-mono text-xs text-subtle">{apiError.requestId}</p> : null}
      </div>
      {onRetry ? (
        <Button variant="outline" onClick={onRetry}>
          <Icon name="refresh" size={18} />
          Retry
        </Button>
      ) : null}
    </div>
  );
}
